import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ActionEditor = ({ action = {}, devices = [], scenes = [], onSave, onCancel }) => {
  const [type, setType] = useState(action.type || 'device');

  // Device action fields
  const [device, setDevice] = useState(action.device || '');
  const [devicePayload, setDevicePayload] = useState(action.payload || {});
  const [payloadKey, setPayloadKey] = useState('');
  const [payloadValue, setPayloadValue] = useState('');
  const [parentId, setParentId] = useState(action.parentId || '');

  // Scene action fields
  const [sceneId, setSceneId] = useState(action.sceneId || '');
  const [silent, setSilent] = useState(action.silent || false);

  // Notification action fields
  const [message, setMessage] = useState(action.message || '');
  const [level, setLevel] = useState(action.level || 'info');

  // Stabilize devices reference to avoid unnecessary re-renders from WebSocket updates
  const stableDevices = useMemo(() => {
    return devices;
  }, [devices]);

  // Get device attributes when device changes
  const [deviceAttributes, setDeviceAttributes] = useState([]);
  
  useEffect(() => {
    if (device) {
      // Find the selected device
      const selectedDevice = stableDevices.find(d => d.id === device);
      
      if (selectedDevice) {
        // Extract available attributes from device
        const attributes = [];
        
        // Add all properties from controls
        if (selectedDevice.controls) {
          selectedDevice.controls.forEach(control => {
            if (control.property && !attributes.includes(control.property)) {
              attributes.push(control.property);
            }
          });
        }
        
        setDeviceAttributes(attributes);
        
        // Check if this is a child device
        if (selectedDevice.parentId && selectedDevice.parentId !== device) {
          setParentId(selectedDevice.parentId);
        } else {
          setParentId('');
        }
      }
    }
  }, [device, stableDevices]);

  const handleAddPayloadItem = () => {
    if (payloadKey && payloadValue) {
      setDevicePayload(prev => ({
        ...prev,
        [payloadKey]: payloadValue
      }));
      setPayloadKey('');
      setPayloadValue('');
    }
  };

  const handleRemovePayloadItem = (key) => {
    const newPayload = { ...devicePayload };
    delete newPayload[key];
    setDevicePayload(newPayload);
  };

  const handleSave = () => {
    let newAction = { type };
    
    if (type === 'device') {
      newAction = {
        ...newAction,
        device,
        payload: devicePayload
      };
      
      // Only include parentId if it exists
      if (parentId) {
        newAction.parentId = parentId;
      }
    } else if (type === 'scene') {
      newAction = {
        ...newAction,
        sceneId,
        silent
      };
    } else if (type === 'notification') {
      newAction = {
        ...newAction,
        message,
        level
      };
    }
    
    onSave(newAction);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Label htmlFor="action-type" className="text-sm font-medium">Action Type</Label>
        <select
          id="action-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="device">Control Device</option>
          <option value="scene">Activate Scene</option>
          <option value="notification">Show Notification</option>
        </select>
      </div>

      {type === 'device' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="device" className="text-sm font-medium">Device</Label>
            <select
              id="device"
              value={device}
              onChange={(e) => {
                const deviceId = e.target.value;
                setDevice(deviceId);
                if (deviceId) {
                  const selectedDevice = devices.find(d => d.id === deviceId);
                  if (selectedDevice && selectedDevice.parentId && selectedDevice.parentId !== deviceId) {
                    setParentId(selectedDevice.parentId);
                  } else {
                    setParentId('');
                  }
                }
              }}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select a device</option>
              {stableDevices.map(d => (
                <option key={d.id} value={d.id}>
                   {`${d.name} (${d.location || 'N/A'}, Floor ${d.floor || 'N/A'})`}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Device Properties</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="payload-key" className="text-xs">Property</Label>
                <select
                  id="payload-key"
                  value={payloadKey}
                  onChange={(e) => setPayloadKey(e.target.value)}
                  disabled={!device}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                >
                  <option value="">Select property</option>
                  {deviceAttributes.map(attr => (
                    <option key={attr} value={attr}>
                      {attr}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="payload-value" className="text-xs">Value</Label>
                <Input
                  id="payload-value"
                  type="text"
                  value={payloadValue}
                  onChange={(e) => setPayloadValue(e.target.value)}
                  disabled={!payloadKey}
                  className="h-10"
                />
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleAddPayloadItem}
              disabled={!payloadKey || !payloadValue}
              className="w-full h-10"
            >
              Add Property
            </Button>

            <div className="space-y-2">
              {Object.keys(devicePayload).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-md">No properties added</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(devicePayload).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 bg-muted/30 max-w-full">
                      <div className="text-xs min-w-0 truncate">
                        <span className="font-medium">{key}</span>
                        <span className="text-muted-foreground ml-1">= {value}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePayloadItem(key)}
                        className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {type === 'scene' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="scene-id" className="text-sm font-medium">Scene</Label>
            <select
              id="scene-id"
              value={sceneId}
              onChange={(e) => setSceneId(e.target.value)}
              required
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select a scene</option>
              {scenes.map(scene => (
                <option key={scene.id} value={scene.id}>
                  {scene.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="silent"
              type="checkbox"
              checked={silent}
              onChange={(e) => setSilent(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="silent" className="text-sm">Silent (no notifications)</Label>
          </div>
        </>
      )}

      {type === 'notification' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="message" className="text-sm font-medium">Message</Label>
            <Input
              id="message"
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              className="h-10"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="level" className="text-sm font-medium">Level</Label>
            <select
              id="level"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        </>
      )}

      <div className="flex gap-2 pt-4 border-t border-muted">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 h-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          className="flex-1 h-10"
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default ActionEditor;